import { HttpClient, HttpHeaders } from '@angular/common/http';
import { EventEmitter, inject, Injectable, Output } from '@angular/core';
import { environment } from 'src/environments/environment';
import { UdlControlledVocabulary } from '../../shared/models/udl-controlled-vocabulary.model';
import { BehaviorSubject, catchError, Observable, map, of } from 'rxjs';
import { UdlModelService } from './udl-model.service';
import Swal from 'sweetalert2';
import { UdlControlledVocabularyService } from './udl-controlled-vocabulary.service';
import { FileContentResult } from '../../shared/models/file-content-result';

@Injectable({
  providedIn: 'root'
})

export class UdlOverlapToolContainerService {
  private modelService: UdlModelService = inject(UdlModelService);
  private PCTSampleFileKey = 'PCTSampleFile';
  private OnOffConsiderationsSampleFileKey = 'OnOffConsiderationsSampleFile';
  private IndirectRelationshipsSampleFileKey = 'IndirectRelationshipsSampleFile';

  private filesToBeUploaded: File[] | undefined = undefined;

  private IsProjectToBeImported: boolean = false;
  private projectToBeImportedFrom: number = 0;

  public projectFilesUpdatedEvent: EventEmitter<number> = new EventEmitter<number>();
  public projectPkAvailableEvent: EventEmitter<number> = new EventEmitter<number>();
  public projectFileDeletedEvent: EventEmitter<SelectedFileNameKeys> = new EventEmitter<SelectedFileNameKeys>();
  public projectFileDeleteCancelledEvent: EventEmitter<SelectedFileNameKeys> = new EventEmitter<SelectedFileNameKeys>();
  public projectFileDownloadedEvent: EventEmitter<string> = new EventEmitter<string>();
  public projectFileDownloadFailedEvent: EventEmitter<void> = new EventEmitter<void>();
  
  public onOffConsiderations: SelectedFileNameKeys.OnOffConsiderations = SelectedFileNameKeys.OnOffConsiderations;
  public pctValues: SelectedFileNameKeys.PCTValues = SelectedFileNameKeys.PCTValues;
  public obligateAndIndirectRelationship: SelectedFileNameKeys.ObligateAndIndirectRelationship = SelectedFileNameKeys.ObligateAndIndirectRelationship;

  constructor(private http: HttpClient, private controlledVocabularyService: UdlControlledVocabularyService) {
    this.projectFilesUpdatedEvent.subscribe((projectPk: number) => {
      this.setProjectPk(projectPk);
    });
  }

  public projectPk: number = 0;
  public setProjectPk(projectPk: number) {
    this.projectPk = projectPk;
    this.projectPkAvailableEvent.emit(this.projectPk);
  }

  /// Update the files first by importing the files from the imported project, removing the files marked for deletion, and then uploading the new ones
  public updateProjectFiles(projectPk: number): void {
    this.copyProjectFilesFromImportedProject(projectPk).subscribe((done: boolean) => {
      this.uploadProjectFiles(projectPk);
    });
  }

  copyProjectFilesFromImportedProject(projectPk: number): Observable<boolean> {
    if (this.IsProjectToBeImported) {
      const url = `${environment.BAMS_Api_URL}api/projects/${projectPk}/epa/model/v${this.modelService.modelVersion}/copyfilesfromimportedproject/${this.projectToBeImportedFrom}`;
      return this.http.post(url, {}).pipe(
        map((data: any) => {
          this.IsProjectToBeImported = false; // Set to false when importing finished successfully
          return data.Value;
        }),
        catchError((error) => {
          console.error("Error - Copy project files failed", error);
          return of(false);
        })
      );
    }
    return of(true);
  }

  setFilesToBeUploaded(filesToBeUploaded: File[]) {
    this.filesToBeUploaded = filesToBeUploaded;
  }

  uploadProjectFiles(projectPk: number): void {
    if (this.filesToBeUploaded) {
      const url = `${environment.BAMS_Api_URL}api/projects/${projectPk}/epa/model/v${this.modelService.modelVersion}/files`;
      const formData = new FormData();
      for (let file of this.filesToBeUploaded) {
        formData.append('files', file, file.name);
      }
      this.http.put(url, formData).subscribe({
        next: () => {
          this.filesToBeUploaded = undefined; // reset
          this.projectFilesUpdatedEvent.emit(projectPk);
        },
        error: (err) => {
          console.error("Error - Files upload failed", err);
        }
      });
    }
    else {
      this.projectFilesUpdatedEvent.emit(projectPk);
    }
  }

  downloadDefaultPCTFile() {
    return this.getFileByKey(this.PCTSampleFileKey);
  }

  downloadDefaultOnOffConsiderationsFile() {
    return this.getFileByKey(this.OnOffConsiderationsSampleFileKey);
  }

  downloadDefaultIndirectRelationshipsFile() {
    return this.getFileByKey(this.IndirectRelationshipsSampleFileKey);
  }

  public downloadProjectFile(projectPk: number, modelVersion: number, filename: string) {
    const url = `${environment.BAMS_Api_URL}api/projects/${projectPk}/epa/model/v${modelVersion}/files/${filename}`;
    this.downloadFile(url);
  }

  public downloadSummaryFile(projectPk: number, modelVersion: number) {
    const url = `${environment.BAMS_Api_URL}api/projects/${projectPk}/epa/model/v${modelVersion}/summaryfile/`;
    this.downloadFile(url);
  }

  private getFileByKey(key: string) {
    return this.controlledVocabularyService.getControlledVocabularyItemsByKey(key).subscribe((data: UdlControlledVocabulary[]) => {
      const filename = data[0].long_value || data[0].short_value || '';
      const url = `${environment.BAMS_Api_URL}api/downloaddefaultfile/${filename}`;
      this.downloadFile(url);
    });
  }

  private downloadFile(url: string) {
    this.http.get<FileContentResult>(url, { responseType: 'json' }).subscribe({
      next: (result: FileContentResult) => {
        this.downloadFileFromFileConentResult(result);
      },
      error: (error) => {
        this.projectFileDownloadFailedEvent.emit();
      }
    });
  }

  private downloadFileFromFileConentResult(fileContentResult: FileContentResult) {
    const binaryData = this.base64ToArrayBuffer(fileContentResult.FileContents);
    const blob = new Blob([binaryData], { type: fileContentResult.ContentType });
    const downloadLink = document.createElement('a');
    downloadLink.href = window.URL.createObjectURL(blob);
    downloadLink.download = fileContentResult.FileDownloadName;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    this.projectFileDownloadedEvent.emit(fileContentResult.FileDownloadName);
  }

  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binaryString = atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }

  public getFilesValidationErrors(projectPk: number){
    const url = `${environment.BAMS_Api_URL}api/projects/${projectPk}/epa/model/v${this.modelService.modelVersion}/validatefiles`;
    return this.http.get<any[]>(url);
  }

  // needed for us to know when we do save operation to let the backend copy the files from the imported project
  public setProjectToBeImported(projectPk: number) {
    this.IsProjectToBeImported = true;
    this.projectToBeImportedFrom = projectPk;
  }

  public isProjectToBeImported(): boolean {
    if (this.projectToBeImportedFrom > 0 && this.IsProjectToBeImported) {
      return true;
    }
    return false;
  }

  public deleteUploadedFile(projectPk: number, uploadedFile: SelectedFileNameKeys, skipConfirmation: boolean = false) {
    if (skipConfirmation) {
      const url = `${environment.BAMS_Api_URL}api/projects/${projectPk}/epa/model/v${this.modelService.modelVersion}/deletefile/${uploadedFile}`;
        this.http.delete(url).subscribe(() => {
          this.projectFileDeletedEvent.emit(uploadedFile);
        });
    }

    else {
        Swal.fire({
        text: 'Are you sure you want to delete this file?',
        showCancelButton: true,
        confirmButtonColor: '#0069be',
        confirmButtonText: 'Yes',
        cancelButtonText: 'No',
        icon: 'warning'
      }).then((result) => {
        if (result.value) {
          const url = `${environment.BAMS_Api_URL}api/projects/${projectPk}/epa/model/v${this.modelService.modelVersion}/deletefile/${uploadedFile}`;
          this.http.delete(url).subscribe(() => {
            this.projectFileDeletedEvent.emit(uploadedFile);
          });
        }
        else this.projectFileDeleteCancelledEvent.emit(uploadedFile);
      });
    }
  }
}

export enum SelectedFileNameKeys {
  OnOffConsiderations = "selectedOnOffConsiderationsFileName",
  PCTValues = "selectedPCTValuesFileName",
  ObligateAndIndirectRelationship = "selectedObligateAndIndirectRelationshipFileName"
}
